import React from 'react';
import { Badge, Button, Card, CardBody, CardHeader } from 'react-bootstrap';
import '../accreditation.scss';
import { IAccreditation } from '../../../interfaces/IAccreditation';
import { IAccreditationApplication } from '../../../interfaces/IAccreditationApplication';
import { AccreditationType, ApplicationStatus } from '../../../graphql/__generated__/graphql';
import { getStatusDisplay, isApplicantEditable } from '../../../utils/applicationStatusUtils';

interface IAccreditationCardProps {
    accreditation: IAccreditation;
    application?: IAccreditationApplication;
    onRenewAccreditation: () => void;
    onViewApplication: () => void;
    onSubmitFinalApplication: () => void;
    onSubmitForReview: () => void;
    renewable?: boolean;
    urgent?: boolean;
}

function generateApplicationButtons(
    application: IAccreditationApplication,
    renewable: boolean,
    reaccreditationPrice: number | undefined,
    onRenewAccreditation: () => void,
    onSubmitFinalApplication: () => void,
    onSubmitForReview: () => void,
    accreditationType: AccreditationType
) {
    if (!isApplicantEditable(application.applicationStatus)) return <></>;

    return (
        <>
            {(application?.applicationStatus === undefined ||
                application?.applicationStatus === ApplicationStatus.ApplicantDraft ||
                application?.applicationStatus ===
                    ApplicationStatus.ApplicantRevisionsFromSupervisor ||
                application?.applicationStatus === ApplicationStatus.ApplicantRevisionsFromEmdr) &&
                renewable &&
                (reaccreditationPrice || 0) > 0 && (
                    <Button variant={'outline-secondary'} onClick={onRenewAccreditation}>
                        {application?.accreditationApplicationId
                            ? `Continue your ${accreditationType.name} reaccreditation application`
                            : `Renew your ${accreditationType.name} accreditation`}
                    </Button>
                )}

            {application?.applicationStatus ===
                ApplicationStatus.ApplicantReadyForSupervisorReview && (
                <Button variant={'outline-secondary'} onClick={onSubmitForReview}>
                    Submit for review
                </Button>
            )}
            {application?.applicationStatus === ApplicationStatus.ApplicantReadyForEmdr && (
                <Button variant={'outline-secondary'} onClick={onSubmitFinalApplication}>
                    Submit to EMDR
                </Button>
            )}
        </>
    );
}

const AccreditationCard: React.FC<IAccreditationCardProps> = ({
    accreditation,
    application,
    onRenewAccreditation,
    onViewApplication,
    onSubmitFinalApplication,
    onSubmitForReview,
    renewable = true,
    urgent = false,
}) => {
    const { accreditationType } = accreditation;

    const reaccreditationPrice = application?.accreditationType.renewalPrice;

    const badgeClass = `app-status-${application?.applicationStatus?.toLowerCase().replaceAll('_', '-')}`;

    return (
        <Card className="mb-3" border="light">
            <CardHeader className={`bg-transparent border-0 p-0`}>
                <div className={'end-pill-wrapper'}>
                    {accreditation.active &&
                        (renewable ? (
                            <Badge
                                pill
                                bg={urgent ? 'danger' : 'warning'}
                                className={'floating-pill'}
                            >
                                Expires {new Date(accreditation.endDate).toLocaleDateString()}
                            </Badge>
                        ) : (
                            <Badge pill bg={'primary'} className={'floating-pill'}>
                                Active{' '}
                                {accreditation.startDate &&
                                    'since ' +
                                        new Date(accreditation.startDate).toLocaleDateString()}
                            </Badge>
                        ))}
                    {accreditation.expired && (
                        <Badge pill bg={'danger'} className={'floating-pill'}>
                            Expired {new Date(accreditation.endDate).toLocaleDateString()}
                        </Badge>
                    )}
                    {application?.accreditationApplicationId && application?.applicationStatus && (
                        <Badge pill bg={badgeClass} className={'floating-pill'}>
                            {getStatusDisplay(application.applicationStatus)}
                        </Badge>
                    )}
                </div>
            </CardHeader>
            <CardBody>
                <h3 className="card-title mb-3 fw-bold">{accreditationType.name} accreditation</h3>
                {isApplicantEditable(application?.applicationStatus) &&
                    renewable &&
                    (reaccreditationPrice || 0) > 0 && (
                        <p className="card-subtitle mb-2 fw-bold text-black">
                            Cost: &pound;{reaccreditationPrice!.toFixed(2)}
                        </p>
                    )}
                <p className="card-text">{accreditationType.description}</p>
                {application &&
                    generateApplicationButtons(
                        application,
                        renewable,
                        reaccreditationPrice,
                        onRenewAccreditation,
                        onSubmitFinalApplication,
                        onSubmitForReview,
                        accreditationType
                    )}
                {application?.accreditationApplicationId &&
                    !isApplicantEditable(application?.applicationStatus) && (
                        <Button variant={'outline-secondary'} onClick={onViewApplication}>
                            View reaccreditation application
                        </Button>
                    )}
            </CardBody>
        </Card>
    );
};

export default AccreditationCard;
